import { FC } from "react";
import { Box, Grid } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
	CalculationMethods,
	EnvironmentalKpi,
	SitesModelType,
	SitesSourceModelType,
} from "../../../../../common/modelTypes";
import { colGrow } from "../../../../../common/utils";
import PieChartDistributionType, { DataChart } from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import ContentLocked from "../../../../../components/common/ContentLocked";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../../../components/common/PermissionGridItem";
import TableData from "../../../../../components/common/table/TableData";
import { DonutChartItemDTO } from "../../../../../datasources/api-client/data-contracts";
import { getLockedContents } from "../../../../../permissions/permissionUtils";
import { useChartColors } from "../../../../../theme/theme";
import { dataColSitesSource, getColumnsSitesSource, getRowsSitesSource } from "../Greenrouter/data";

interface Props {
	emissionsSites: SitesModelType;
	calculationMethods: CalculationMethods;
	nodesCertification: CalculationMethods;
	environmentalKpi: EnvironmentalKpi;
	siteSource: SitesSourceModelType;
}

const ThirdParty: FC<Props> = ({
	emissionsSites,
	calculationMethods,
	nodesCertification,
	environmentalKpi,
	siteSource,
}) => {
	const { t } = useTranslation("feature.units");
	const { chartColors } = useChartColors();

	// Color palette matching data order
	// The order is IMPORTANT
	const colors = [
		chartColors.method1,
		chartColors.method2,
		chartColors.method3,
		chartColors.method4,
		chartColors.method5,
		chartColors.method6,
	];

	const getDataChart = (data: DonutChartItemDTO[], t: TFunction): DataChart[] => {
		return (
			data.map((value, index) => {
				return {
					id: index,
					percent: value.percentage ? Number(value.percentage.toFixed(2)) : 0,
					value: value.value ?? 0,
					label: value.label || "",
					icon: <Box borderRadius={0.8} width={12} height={12} sx={{ backgroundColor: colors[index] }} />,
				};
			}) || []
		);
	};

	const marketBase: MarketBaseType = {
		emissions: emissionsSites.data?.thirdParty?.current.marketBasedWtw,
		delta: emissionsSites.data?.thirdParty?.current.marketBasedWtwDelta,
		coverage: emissionsSites.data?.thirdParty?.current.marketBasedWtwCoverage,
		isLoading: emissionsSites.isLoading,
		onReload: emissionsSites.reload,
	};

	const titles = {
		totalEmissions: t("overview/total emissions"),
		operationalUnits: t("overview/no operational units"),
		methodology: t("thirdParty/methodology used"),
		certification: t("thirdParty/certification presence"),
		emissionsBySource: t("greenrouter/emissions and consumption by source"),
		environmentalKpis: t("common:environmental kpis"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [emissionsSites],
			names: [titles.totalEmissions, titles.operationalUnits, titles.environmentalKpis, titles.marketBased],
		},
		{ models: [calculationMethods], names: [titles.methodology] },
		{ models: [nodesCertification], names: [titles.certification] },
		{ models: [siteSource], names: [titles.emissionsBySource] },
	]);

	return (
		<Grid container spacing={2}>
			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[emissionsSites]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.totalEmissions}
						value={emissionsSites.data?.thirdParty?.current.co2Wtw.value}
						delta={emissionsSites.data?.thirdParty?.delta.co2Wtw}
						measure={emissionsSites.data?.thirdParty?.current.co2Wtw.measure}
						isLoading={emissionsSites.isLoading}
						onReload={emissionsSites.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[emissionsSites]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.operationalUnits}
						value={emissionsSites.data?.thirdParty?.current.count}
						isLoading={emissionsSites.isLoading}
						onReload={emissionsSites.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[calculationMethods]} size={colGrow} minHeight={320}>
					<PieChartDistributionType
						totalEmissions={calculationMethods.data?.total.value}
						keyEmissions={calculationMethods.data?.total.measure}
						title={titles.methodology}
						isLoading={calculationMethods.isLoading}
						onReload={calculationMethods.reload}
						dataChart={calculationMethods.data?.items && getDataChart(calculationMethods.data.items, t)}
						colors={colors}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[nodesCertification]} size={colGrow} minHeight={320}>
					<PieChartDistributionType
						totalEmissions={nodesCertification.data?.total.value}
						keyEmissions={nodesCertification.data?.total.measure}
						title={titles.certification}
						isLoading={nodesCertification.isLoading}
						onReload={nodesCertification.reload}
						dataChart={nodesCertification.data?.items && getDataChart(nodesCertification.data.items, t)}
						colors={colors}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[siteSource]} size={colGrow} minHeight={400}>
					<TableData
						columns={getColumnsSitesSource(dataColSitesSource, t)}
						rows={siteSource.data ? getRowsSitesSource(siteSource.data, t) : []}
						title={titles.emissionsBySource}
						isLoading={siteSource.isLoading}
						onReload={siteSource.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[emissionsSites]} size={12}>
				<AmbientalKpiWidget
					bio={emissionsSites.data?.thirdParty.current.biogenic}
					bioCoverage={emissionsSites.data?.thirdParty.current.biogenicCoverage}
					loading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</PermissionGridItem>

			<PermissionGridItem models={[emissionsSites]} size={12}>
				<MarketBaseWidget marketBase={marketBase} />
			</PermissionGridItem>

			{lockedContents.length > 0 && (
				<Grid size={12}>
					<ContentLocked items={lockedContents} />
				</Grid>
			)}
		</Grid>
	);
};

export default ThirdParty;
